import React, { useState, useEffect } from 'react';
import { Upload, Download, Database, FileText, AlertCircle, CheckCircle, XCircle, Filter, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { importService } from '../../services/importService';
import { crmService } from '../../services/crmService';
import ImportWizard from './components/ImportWizard';
import ImportHistory from './components/ImportHistory';
import LeadAuditTrail from './components/LeadAuditTrail';
import DataValidationPanel from './components/DataValidationPanel';
import ExportPanel from './components/ExportPanel';
import BulkOperationsPanel from './components/BulkOperationsPanel';

const LeadDataManagementImportCenter = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [leads, setLeads] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalLeads: 0,
    importedToday: 0,
    duplicatesFound: 0,
    validationErrors: 0
  });
  const [filters, setFilters] = useState({
    source: '',
    campaign: '',
    status: '',
    importBatch: ''
  });

  useEffect(() => {
    if (userProfile?.id) {
      loadData();
    }
  }, [userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load leads
      const leadsData = await crmService?.getLeads(userProfile?.id);
      setLeads(leadsData || []);

      // Load import history
      const historyData = await importService?.getImportHistory(userProfile?.id);
      setImportHistory(historyData || []);

      // Calculate stats
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      const importedToday = historyData?.filter(imp => 
        new Date(imp?.createdAt) >= today
      )?.reduce((sum, imp) => sum + (imp?.successfulImports || 0), 0);

      const duplicatesFound = historyData?.reduce((sum, imp) => 
        sum + (imp?.duplicatesFound || 0), 0
      );

      const validationErrors = historyData?.reduce((sum, imp) => 
        sum + (imp?.failedImports || 0), 0
      );

      setStats({
        totalLeads: leadsData?.length || 0,
        importedToday,
        duplicatesFound,
        validationErrors
      });
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleImportComplete = () => {
    loadData();
    setActiveTab('history');
  };

  const handleExport = async () => {
    try {
      await importService?.exportLeads({
        advisorId: userProfile?.id,
        ...filters
      });
    } catch (err) {
      setError(err?.message || 'Export failed');
    }
  };

  const filteredLeads = leads?.filter(lead => {
    if (filters?.source && lead?.fuente !== filters?.source) return false;
    if (filters?.status && lead?.stage !== filters?.status) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data management center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Lead Data Management & Import Center</h1>
        <p className="text-gray-600">Comprehensive lead database consolidation and bulk import capabilities</p>
      </div>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Leads</p>
              <p className="text-3xl font-bold text-gray-900">{stats?.totalLeads}</p>
            </div>
            <Database className="w-10 h-10 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Imported Today</p>
              <p className="text-3xl font-bold text-green-600">{stats?.importedToday}</p>
            </div>
            <TrendingUp className="w-10 h-10 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Duplicates Found</p>
              <p className="text-3xl font-bold text-yellow-600">{stats?.duplicatesFound}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Validation Errors</p>
              <p className="text-3xl font-bold text-red-600">{stats?.validationErrors}</p>
            </div>
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('import')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'import' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Import Wizard
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'export'
                  ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Download className="w-4 h-4 inline mr-2" />
              Export
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'history' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Import History
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'audit' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <CheckCircle className="w-4 h-4 inline mr-2" />
              Audit Trail
            </button>
            <button
              onClick={() => setActiveTab('validation')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'validation' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Data Validation
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'bulk' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Filter className="w-4 h-4 inline mr-2" />
              Bulk Operations
            </button>
          </nav>
        </div>
      </div>
      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Consolidated Lead Database</h2>
            <div className="mb-6">
              <div className="flex gap-4 mb-4">
                <select
                  value={filters?.source}
                  onChange={(e) => setFilters({ ...filters, source: e?.target?.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Sources</option>
                  <option value="referral">Referral</option>
                  <option value="website">Website</option>
                  <option value="social_media">Social Media</option>
                  <option value="direct">Direct</option>
                  <option value="event">Event</option>
                  <option value="other">Other</option>
                </select>

                <select
                  value={filters?.status}
                  onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal</option>
                  <option value="negotiation">Negotiation</option>
                  <option value="closed_won">Closed Won</option>
                  <option value="closed_lost">Closed Lost</option>
                </select>

                <button
                  onClick={handleExport}
                  className="ml-auto px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export Filtered
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Phone</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Source</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Stage</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Interest</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads?.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-gray-500">
                        No leads found
                      </td>
                    </tr>
                  ) : (
                    filteredLeads?.map((lead) => (
                      <tr key={lead?.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">{lead?.nombre}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{lead?.email}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{lead?.telefono || '-'}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {lead?.fuente}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                            {lead?.stage}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            lead?.nivelInteres === 'hot' ? 'bg-red-100 text-red-800' :
                            lead?.nivelInteres === 'warm'? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {lead?.nivelInteres}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(lead?.createdAt)?.toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'import' && (
          <ImportWizard 
            advisorId={userProfile?.id} 
            onComplete={handleImportComplete}
          />
        )}

        {activeTab === 'export' && (
          <ExportPanel 
            advisorId={userProfile?.id}
            leads={leads}
          />
        )}

        {activeTab === 'history' && (
          <ImportHistory 
            history={importHistory}
            onRefresh={loadData}
          />
        )}

        {activeTab === 'audit' && (
          <LeadAuditTrail 
            leads={leads}
          />
        )}

        {activeTab === 'validation' && (
          <DataValidationPanel 
            leads={leads}
          />
        )}

        {activeTab === 'bulk' && (
          <BulkOperationsPanel 
            leads={filteredLeads}
            advisorId={userProfile?.id}
            onComplete={loadData}
          />
        )}
      </div>
    </div>
  );
};

export default LeadDataManagementImportCenter;